import {User} from "../cdx-models/User";
import {by} from "./sort";

const getMaxDiffInMs = (date: Date | null) => {
  if (!date) return 1000 * 3600 * 12;
  const diffInDays = (new Date().getTime() - date.getTime()) / (1000 * 3600 * 24);
  if (diffInDays < 0.2) return 1000 * 3600 * 0.5;
  if (diffInDays < 1) return 1000 * 3600 * 2;
  return 1000 * 3600 * 12;
};

export type UserGroup<T> = {
  user: User | null;
  items: T[];
};

export type TimeGroup<T> = {
  newestDate: Date | null;
  oldestDate: Date | null;
  maxDiffInMs: number;
  diffGroups: UserGroup<T>[];
};

// shape of items: [{user: User1, date: ..., ...}]
export default function timelineSplitter<T>(
  items: T[],
  opts: {
    extractDate: (item: T) => Date | null;
    extractUser: (item: T) => User | null;
    isSorted?: boolean;
  } = {
    extractDate: (i: any) => i.date,
    extractUser: (i: any) => i.user,
    isSorted: false,
  }
) {
  if (!items.length) return [];
  const groups: TimeGroup<T>[] = [];
  const {extractDate, extractUser, isSorted} = opts;
  const itemsWithExtras = items
    .map((item) => ({item, date: extractDate(item), user: extractUser(item)}))
    .filter((el) => el.date);
  if (itemsWithExtras.length === 0) return [];
  if (!isSorted) itemsWithExtras.sort(by((e: any) => e.date, {reversed: true}));
  const firstItem = itemsWithExtras[0];
  let currentDiffGroup: UserGroup<T> = {user: firstItem.user, items: [firstItem.item]};
  let currentGroup: TimeGroup<T> = {
    newestDate: firstItem.date,
    oldestDate: firstItem.date,
    maxDiffInMs: getMaxDiffInMs(firstItem.date),
    diffGroups: [currentDiffGroup],
  };
  groups.push(currentGroup);
  itemsWithExtras.slice(1).forEach(({user, item, date}) => {
    const getDiff = () => {
      if (!currentGroup.oldestDate) return 0;
      if (!date) return 0;
      return currentGroup.oldestDate.getTime() - date.getTime();
    };
    const diffToPreviousGroupInMs = getDiff();
    if (diffToPreviousGroupInMs > currentGroup.maxDiffInMs) {
      currentDiffGroup = {user, items: [item]};
      currentGroup = {
        newestDate: date,
        oldestDate: date,
        maxDiffInMs: getMaxDiffInMs(date),
        diffGroups: [currentDiffGroup],
      };
      groups.push(currentGroup);
    } else {
      currentGroup.oldestDate = date;
      if (user !== currentDiffGroup.user) {
        currentDiffGroup = {user, items: [item]};
        currentGroup.diffGroups.push(currentDiffGroup);
      } else {
        currentDiffGroup.items.push(item);
      }
    }
  });
  return groups;
}
